// Shipper rules — country → allowed shipper master list (Kalpana UAT
// round 4 carry-over from G4). Same shape as Product Mapping admin page.

import { useEffect, useState } from 'react';
import { shipperValidationApi, downloadAuthed } from '../api/services';
import toast from 'react-hot-toast';
import { Plus, Loader2, X, Upload, Download, Search } from 'lucide-react';

export default function AdminShipperValidation() {
  const [rules, setRules] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [form, setForm] = useState({ country: '', allowedShipper: '' });
  const [adding, setAdding] = useState(false);

  const [bulkFile, setBulkFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  async function load(s = search) {
    setLoading(true);
    try {
      const res = await shipperValidationApi.list({ search: s, limit: 1000 });
      setRules(res.data.rules || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load');
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function add() {
    if (!form.country.trim() || !form.allowedShipper.trim()) {
      toast.error('Country and allowed shipper are required'); return;
    }
    setAdding(true);
    try {
      await shipperValidationApi.add({
        country: form.country.trim(),
        allowedShipper: form.allowedShipper.trim(),
      });
      toast.success('Added');
      setForm({ country: '', allowedShipper: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setAdding(false); }
  }

  async function remove(id) {
    if (!window.confirm('Remove this rule?')) return;
    try {
      await shipperValidationApi.remove(id);
      toast.success('Removed');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function uploadBulk() {
    if (!bulkFile) { toast.error('Pick a CSV first'); return; }
    setUploading(true); setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      const res = await shipperValidationApi.bulkUpload(fd);
      setBulkResult(res.data);
      toast.success(`Inserted ${res.data.inserted}, skipped ${res.data.skipped.length}`);
      setBulkFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk upload failed');
    } finally { setUploading(false); }
  }

  function runSearch() { setSearch(searchInput); load(searchInput); }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <p className="text-sm text-gray-500 mb-4">
        Country → allowed shipper rules. New MBO orders whose destination
        country has no rule are flagged and routed to Dispatch Admin.
        The vendor XLSX uses the configured shipper code (LP, EMS,
        Airmail, …) for each parcel. Empty list = validation skipped.
      </p>

      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <div className="text-sm font-semibold mb-2">Bulk upload</div>
        <div className="flex flex-wrap items-end gap-2">
          <input type="file" accept=".csv"
                 onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                 className="text-sm" />
          <button onClick={uploadBulk} disabled={!bulkFile || uploading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload CSV
          </button>
          <button onClick={() => downloadAuthed(shipperValidationApi.sampleUrl().replace('/api',''),
                                                 'shipper-rules-sample.csv').catch(() =>
                  toast.error('Sample download failed'))}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            <Download className="w-4 h-4" /> Download sample
          </button>
          <button onClick={() => downloadAuthed(shipperValidationApi.exportUrl().replace('/api',''),
                                                 `shipper-rules-${new Date().toISOString().slice(0,10)}.csv`).catch(() =>
                  toast.error('Export failed'))}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            <Download className="w-4 h-4" /> Export current rules
          </button>
          <span className="text-xs text-gray-500 ml-auto">
            CSV columns: <code>country, allowed_shipper</code>
          </span>
        </div>
        {bulkResult && (
          <div className="mt-3 text-sm">
            <div className="text-green-700 font-semibold">
              ✓ Inserted {bulkResult.inserted}.
            </div>
            {bulkResult.skipped.length > 0 && (
              <details className="mt-2 text-xs text-amber-700">
                <summary>{bulkResult.skipped.length} rows skipped</summary>
                <ul className="mt-1 ml-4 list-disc">
                  {bulkResult.skipped.slice(0, 50).map((s, i) => (
                    <li key={i}>Row {s.row_num}: {s.reason}</li>
                  ))}
                  {bulkResult.skipped.length > 50 && <li>…and {bulkResult.skipped.length - 50} more</li>}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <div className="text-sm font-semibold mb-2">Add single rule</div>
        <div className="flex gap-2 items-end flex-wrap">
          <label className="flex-1 min-w-[200px]">
            <span className="block text-xs text-gray-500 mb-1">Country</span>
            <input value={form.country}
                   onChange={(e) => setForm({ ...form, country: e.target.value })}
                   placeholder="e.g. United States"
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
          <label className="w-40">
            <span className="block text-xs text-gray-500 mb-1">Allowed shipper</span>
            <input value={form.allowedShipper}
                   onChange={(e) => setForm({ ...form, allowedShipper: e.target.value })}
                   placeholder="LP / EMS / Airmail"
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
          <button onClick={add} disabled={adding}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <Search className="w-4 h-4 text-gray-400" />
          <input value={searchInput}
                 onChange={(e) => setSearchInput(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                 placeholder="Search country or shipper…"
                 className="flex-1 px-2 py-1 text-sm focus:outline-none" />
          <button onClick={runSearch}
                  className="text-sm text-blue-600 hover:underline">Search</button>
          <span className="text-xs text-gray-500 ml-auto">{total} active</span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
          </div>
        ) : rules.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No rules. Bulk upload or add manually above.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Country</th>
                <th className="text-left px-4 py-2">Allowed shipper</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rules.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">{r.country}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.allowed_shipper}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => remove(r.id)}
                            className="text-gray-400 hover:text-red-600"
                            title="Remove">
                      <X className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
